import { useState, useEffect, useEffectEvent } from "react";
import { useAuthenticatedCommissionService } from "../services/commission-authenticated";
import type {
  SalespersonCommission,
  CommissionPeriod,
  CommissionStats,
  CreditNoteSummary
} from "@/shared/types";

interface UseCommissionDataParams {
  period?: { year: number; month: number };
  includeStats?: boolean;
  useAggregations?: boolean;
  isAdmin?: boolean;
}

interface UseCommissionDataResult {
  salespersons: SalespersonCommission[];
  period: CommissionPeriod | null;
  stats: CommissionStats | null;
  excludedSummary: any | null;
  creditNoteSummary: CreditNoteSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCommissionData(params: UseCommissionDataParams = {}): UseCommissionDataResult {
  const [salespersons, setSalespersons] = useState<SalespersonCommission[]>([]);
  const [period, setPeriod] = useState<CommissionPeriod | null>(null);
  const [stats, setStats] = useState<CommissionStats | null>(null);
  const [excludedSummary, setExcludedSummary] = useState<any | null>(null);
  const [creditNoteSummary, setCreditNoteSummary] = useState<CreditNoteSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const commissionService = useAuthenticatedCommissionService();

  // Extract non-reactive logic into useEffectEvent
  // This allows us to access params without adding them all to dependencies
  const onFetchData = useEffectEvent(async () => {
    try {
      setLoading(true);
      setError(null);

      // Can access params directly without dependencies!
      const data = await commissionService.getSalespersons(params);

      setSalespersons(data.salespersons);
      setPeriod(data.period);
      setStats(data.stats || null);
      setExcludedSummary(data.excludedSummary || null);
      setCreditNoteSummary(data.creditNoteSummary || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch commission data");
      console.error("Error fetching commission data:", err);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    onFetchData();
  }, [
    // Only include truly reactive dependencies
    // Other params (includeStats, useAggregations, isAdmin) are accessed via closure
    params.period?.year,
    params.period?.month,
  ]);

  return {
    salespersons,
    period,
    stats,
    excludedSummary,
    creditNoteSummary,
    loading,
    error,
    refetch: onFetchData, // Can use the event directly
  };
}